/**
 * Events & Decor — florists, decorators, tent and chair hire: the bouquet
 * sold off the shelf, the room set up for a proposal, the marquee and the
 * Tiffany chairs out for a Saturday kwanjula.
 *
 * Products ride the FASHION attribute template and variant engine (colours ×
 * sizes, variant ids "variant-<colorId>-<size>"), with colour optional; their
 * sizes come from the "Event sizes" system below.
 *
 * Plain TypeScript — imported by the React app and Convex.
 */

export const EVENTS_INDUSTRY_SLUG = "events" as const;

/**
 * Platform categories for the industry — seeded once by
 * `events/seed:seedEventsCategories`. Slugs are events-specific because the
 * slug index is shared by every industry: the fashion host already has its
 * own "Event Decor" shelf (a kids' centre uses it), and a bare "balloons"
 * would be the first slug anyone else reaches for.
 */
export const EVENTS_CATEGORY_SEED: ReadonlyArray<{ name: string; slug: string; icon: string; description: string }> = [
  { name: "Flowers & Bouquets", slug: "flowers-bouquets", icon: "💐", description: "Roses, gypsophila, bridal, sympathy and giant bouquets" },
  { name: "Gift Boxes & Hampers", slug: "gift-boxes-hampers", icon: "🎁", description: "Flower boxes, chocolate boxes, hampers and gift sets" },
  { name: "Balloons", slug: "events-balloons", icon: "🎈", description: "Latex, foil and number balloons, arches, garlands and walls" },
  { name: "Room & Venue Décor", slug: "room-venue-decor", icon: "✨", description: "Room setups, backdrops, table, stage and aisle décor" },
  { name: "Tents & Structures", slug: "tents-structures", icon: "🎪", description: "Marquees, dome and pagoda tents, canopies, truss and dance floors" },
  { name: "Furniture Hire", slug: "furniture-hire", icon: "🪑", description: "Chairs, tables, lounge sets, covers and linen" },
  { name: "Lighting & Sound", slug: "lighting-sound", icon: "💡", description: "Fairy lights, chandeliers, stage effects, PA systems, generators" },
  { name: "Tableware & Catering Hire", slug: "tableware-catering-hire", icon: "🍽️", description: "Plates, cutlery, glasses, chafing dishes, cake stands, tea urns" },
  { name: "Gifts & Add-ons", slug: "gifts-add-ons", icon: "🎀", description: "Perfume, chocolates, cards, teddies and candles" },
  { name: "Event Services", slug: "event-services", icon: "🎊", description: "Decoration, planning, setup and florist services" },
];

/** One scale for a bouquet, a balloon arch or a room setup. */
export const EVENT_SIZES: ReadonlyArray<string> = ["Mini", "Small", "Medium", "Large", "Extra Large", "Standard", "Custom"];

/**
 * The "Event sizes" size system — created and mapped onto every events
 * category by `events/sizes:seedEventSizes`. `industry` is "all", not
 * "events": the Studio asks for size systems by the product's ATTRIBUTE
 * TEMPLATE ("fashion" for events), so a row tagged "events" would never be
 * offered. `category` "none" files it with One Size under "Other / Specs" in
 * Platform Admin rather than with the clothing or bag scales.
 */
export const EVENT_SIZE_SYSTEM = {
  name: "event_sizes",
  slug: "event-sizes",
  label: "Event sizes",
  description: "Bouquets, arrangements, balloon décor and room setups — Mini to Extra Large, one Standard size, or Custom",
  category: "none",
  industry: "all",
  sizes: EVENT_SIZES,
} as const;

/**
 * Perfume sold as an add-on is still sold by the bottle: the shelf below
 * maps to the existing ml scale instead of event sizes.
 */
export const EVENTS_FRAGRANCES_SHELF = { category: "Gifts & Add-ons", subcategory: "Fragrances" } as const;
export const PERFUME_VOLUME_SIZE_SYSTEM_SLUG = "perfume-volume";
